import { exportChartDocument, exportPreferencesDocument, readChartDocument, readPreferencesDocument } from "./personalData";
import { musicalConfigurationReducers } from "./musicalConfiguration";
export { guitarSetups, selectNpsValues } from "./chartOptions";
import { createSlice, configureStore } from "@reduxjs/toolkit";
import { refineHistoryActions, withRefineHistory } from "./refineHistory";
import { chartHistoryActions, withChartHistory } from "./chartHistory";
import { boundaryTransferCount } from "../components/code/BoundaryNoteMove";

const pruneOmittedNotes = chart => {
  if (!chart.omittedNotes) return;
  const total = chart.notesPerString.reduce((sum, count) => sum + count, 0);
  const kept = chart.omittedNotes.filter(id => id < total);
  if (kept.length !== chart.omittedNotes.length) chart.omittedNotes = kept;
};

const initialState = {
  tinyScreen: false,
  accountChartDefaults: null,
  fretboardShowNoteNames: true,
  fretboardSoundEnabled: true,
  metronomeStartingBpm: 120,
  metronomeRamping: true,
  metronomeVolume: 80,
  title: "Put your chart name here...",
  introduction: "",
  imageScale: 75.0,

  isQueryStringUnpacked: false,

  intervals: [
    true,
    false,
    true,
    false,
    true,
    true,
    false,
    true,
    false,
    true,
    false,
    true,
  ],
  intervalColors: [
    "#000000",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
    "#ffffff",
  ],

  guitarSetup: "EADGBE",

  selectNps: "auto",

  // the tuning of the neck from low to high.
  tuning: [5, 5, 5, 4, 5],

  // root note of the tuning as a midi number
  midiRootNote: 40,

  refineData: {
    perChartData: [],
  },

  createdData: [],
  publishedData: [],

  queryString: "",
  menuSelected: 0,
  protectRefineData: true,

  // the index of the chart being used for ear training
  earTrainerChart: 0,

  // 0 = Text only, 1 = Text and Sounds, 2 = Sounds Only
  earTrainerQuestionType: 1,

  // 0 = ShowAllNotes
  // 1 = Show Only Roots
  // 2 = Show only low Root
  // 3 = Show only High Root
  earTrainerShowNotes: 0,

  // true, plays the chart, false, does Ear training
  // 0 means play the chart,
  // 1 means test mode
  earTrainerMode: 0,

  // 0 means never change, else the integer is the
  // number of notes before moving on.
  earTrainerShapeChange: 0,

  // the note of scale set.
  earTrainerRootNote: 40,

  // the note of scale set. 
  // 0 = lowest Note
  // 1 = highest Note
  earTrainerRootOctaveSelect: 0,

  // should I care about octaves in the answer
  earTrainerIgnoreOctaves: 0,

  // Fretboard specific data
  fretboardNotesDisplay: [
    true,
    false,
    true,
    false,
    true,
    true,
    false,
    true,
    false,
    true,
    false,
    true,
  ],

  fretboardUseSharps: 0,
};

// create a slice of our global state
export const defaultPreferencesDocument = exportPreferencesDocument(initialState);

const mainSlice = createSlice({
  name: "main",
  initialState,
  reducers: {
    ...musicalConfigurationReducers,
    startNewChart(state) {
      Object.assign(state, exportChartDocument(initialState).chart, state.accountChartDefaults || {});
      state.menuSelected = 1;
      state.protectRefineData = false;
      state.isQueryStringUnpacked = true;
      state.queryString = "";
      state.createdData = [];
      state.publishedData = [];
    },
    loadChartDocument(state, action) {
      const document = readChartDocument(action.payload);
      if (!document) return;
      Object.assign(state, document.chart);
      state.protectRefineData = true;
      state.isQueryStringUnpacked = true;
      state.menuSelected = 2;
      state.queryString = "";
      state.createdData = [];
      state.publishedData = [];
    },
    loadPreferencesDocument(state, action) {
      const document = readPreferencesDocument(action.payload);
      if (!document) return;
      state.accountChartDefaults = document.chartDefaults;
      Object.assign(state, document.earTrainer, document.fretboard, document.metronome);
    },
    saveCurrentChartDefaults(state) {
      state.accountChartDefaults = exportPreferencesDocument(state).chartDefaults;
    },
    setFretboardSoundEnabled(state, action) {
      if (typeof action.payload === "boolean") state.fretboardSoundEnabled = action.payload;
    },
    setFretboardShowNoteNames(state, action) {
      if (typeof action.payload === "boolean") state.fretboardShowNoteNames = action.payload;
    },
    setMetronomeStartingBpm(state, action) {
      if (!Number.isFinite(action.payload)) return;
      state.metronomeStartingBpm = Math.min(400, Math.max(10, action.payload));
    },
    setMetronomeRamping(state, action) {
      if (typeof action.payload === "boolean") state.metronomeRamping = action.payload;
    },
    setMetronomeVolume(state, action) {
      if (!Number.isFinite(action.payload)) return;
      state.metronomeVolume = Math.min(100, Math.max(0, action.payload));
    },
    setTinyScreen(state, action) {
      const payload = action.payload;
      state.tinyScreen = payload;
    },
    setProtectRefineData(state, action) {
      const payload = action.payload;
      state.protectRefineData = payload;
    },

    setCreatedData(state, action) {
      const payload = action.payload;
      state.createdData = payload;
    },

    setPublishedData(state, action) {
      const payload = action.payload;
      state.publishedData = payload;
    },

    setTitle(state, action) {
      const payload = action.payload;
      state.title = payload;
    },

    setIntroduction(state, action) {
      const payload = action.payload;
      state.introduction = payload;
    },

    setImageScale(state, action) {
      const payload = +action.payload;
      state.imageScale = payload;
    },

    setRefineData(state, action) {
      const payload = action.payload;
      state.refineData = payload;
      //console.log(payload);
    },

    deleteRefineDataElement(state, action) {
      const index = action.payload;
      state.refineData.perChartData.splice(index, 1);
    },

    duplicateRefineDataElement(state, action) {
      const index = action.payload;
      let dup = { ...state.refineData.perChartData[index] };
      state.refineData.perChartData.splice(index, 0, dup);
    },

    trimRefineChartString(state, action) {
      const strIndex = action.payload.strIndex;
      const chartIndex = action.payload.chartIndex;
      const offset = action.payload.offset;

      if (strIndex < 0 || chartIndex < 0) return;

      if (state.refineData.perChartData.length > chartIndex) {
        const chart = state.refineData.perChartData[chartIndex];
        if (chart.notesPerString.length > strIndex) {
          const notes = chart.notesPerString[strIndex] + offset;
          if (notes >= 0 && notes <= 8) {
            //state.refineData.perChartData[chartIndex].notesPerString[strIndex] = notes;
            chart.notesPerString[strIndex] = notes;
            pruneOmittedNotes(chart);
          }
        }
      }
    },

    adjustRefineChartStrings(state, action) {
      // A +/- click moves notes across two strings as one undoable operation.
      for (const { strIndex, chartIndex, offset } of action.payload) {
        const chart = state.refineData.perChartData[chartIndex];
        if (!chart || strIndex < 0 || strIndex >= chart.notesPerString.length) continue;
        const notes = chart.notesPerString[strIndex] + offset;
        if (notes >= 0 && notes <= 8) chart.notesPerString[strIndex] = notes;
      }
      for (const chartIndex of new Set(action.payload.map(edit => edit.chartIndex))) {
        const chart = state.refineData.perChartData[chartIndex];
        if (chart) pruneOmittedNotes(chart);
      }
    },

    moveRefineBoundary(state, action) {
      const { chartIndex, from, to, noteId } = action.payload;
      const chart = state.refineData.perChartData[chartIndex];
      if (!chart) return;
      const amount = boundaryTransferCount(chart, from, to, noteId);
      if (!amount) return;
      chart.notesPerString[from] -= amount;
      chart.notesPerString[to] += amount;
    },

    omitRefineNote(state, action) {
      const { chartIndex, noteId } = action.payload;
      const chart = state.refineData.perChartData[chartIndex];
      if (!chart || !Number.isSafeInteger(noteId) || noteId < 0 ||
          noteId >= chart.notesPerString.reduce((sum, count) => sum + count, 0) ||
          chart.omittedNotes?.includes(noteId)) return;
      if (!chart.omittedNotes) chart.omittedNotes = [];
      chart.omittedNotes.push(noteId);
    },

    setQueryString(state, action) {
      const args = action.payload;
      state.queryString = args;
    },

    setMenuSelected(state, action) {
      const args = action.payload;
      state.menuSelected = args;
    },
    setIsQueryStringUnpacked(state, action) {
      const args = action.payload;
      state.isQueryStringUnpacked = args;
    },
    setEarTrainerChart(state, action) {
      const args = action.payload;
      state.earTrainerChart = args;
    },
    setEarTrainerQuestionType(state, action) {
      const args = action.payload;
      state.earTrainerQuestionType = args;
    },
    setEarTrainerShowNotes(state, action) {
      const args = action.payload;
      state.earTrainerShowNotes = args;
    },
    setEarTrainerMode(state, action) {
      const args = action.payload;
      state.earTrainerMode = args;
    },
    setEarTrainerShapeChange(state, action) {
      const args = action.payload;
      state.earTrainerShapeChange = args;
    },
    setEarTrainerRootNote(state, action) {
      const args = action.payload;
      state.earTrainerRootNote = args;
    },
    setEarTrainerRootOctaveSelect(state, action) {
      const args = action.payload;
      state.earTrainerRootOctaveSelect = args;
    },
    setEarTrainerIgnoreOctaves(state, action) {
      const args = action.payload;
      state.earTrainerIgnoreOctaves = args;
    },
    setFretboardNotesDisplay(state, action) {
      const payload = action.payload;
      state.fretboardNotesDisplay[payload.index] = payload.value;
    },
    setAllFretboardNotesDisplay(state, action) {
      const payload = action.payload;
      state.fretboardNotesDisplay = payload;
    },
    setFretboardUseSharps(state, action) {
      const payload = action.payload;
      state.fretboardUseSharps = payload;
    },
  },
});

export const mainReducer = withChartHistory(withRefineHistory(mainSlice.reducer));

const store = configureStore({
  reducer: mainReducer,
});

export const mainActions = { ...mainSlice.actions, ...refineHistoryActions, ...chartHistoryActions };

export default store;
